import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_BASE = `${BACKEND_URL}/api`;

const TK_ACCESS = "tg_admin_access";
const TK_REFRESH = "tg_admin_refresh";
const TK_USER = "tg_admin_user";

export const tokenStore = {
  get access() { return localStorage.getItem(TK_ACCESS); },
  get refresh() { return localStorage.getItem(TK_REFRESH); },
  get user() { try { return JSON.parse(localStorage.getItem(TK_USER) || "null"); } catch { return null; } },
  set({ access_token, refresh_token, user }) {
    if (access_token) localStorage.setItem(TK_ACCESS, access_token);
    if (refresh_token) localStorage.setItem(TK_REFRESH, refresh_token);
    if (user) localStorage.setItem(TK_USER, JSON.stringify(user));
  },
  clear() { localStorage.removeItem(TK_ACCESS); localStorage.removeItem(TK_REFRESH); localStorage.removeItem(TK_USER); }
};

export const adminApi = axios.create({ baseURL: ADMIN_BASE, timeout: 15000 });

adminApi.interceptors.request.use((cfg) => {
  const t = tokenStore.access;
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let refreshing = null;
adminApi.interceptors.response.use(
  r => r,
  async (err) => {
    const orig = err.config || {};
    if (err.response?.status === 401 && !orig._retry && tokenStore.refresh) {
      orig._retry = true;
      try {
        if (!refreshing) refreshing = axios.post(`${ADMIN_BASE}/auth/refresh`, { refresh_token: tokenStore.refresh });
        const { data } = await refreshing;
        refreshing = null;
        tokenStore.set({ access_token: data.access_token });
        orig.headers.Authorization = `Bearer ${data.access_token}`;
        return adminApi(orig);
      } catch (e) {
        refreshing = null;
        tokenStore.clear();
        if (typeof window !== "undefined") window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (email, password) => adminApi.post("/auth/login", { email, password }).then(r => r.data),
  me: () => adminApi.get("/auth/me").then(r => r.data),
  logout: () => adminApi.post("/auth/logout").then(r => r.data),
  sessions: () => adminApi.get("/auth/sessions").then(r => r.data),
  revokeSession: (id) => adminApi.delete(`/auth/sessions/${id}`).then(r => r.data),
  revokeOthers: () => adminApi.post("/auth/sessions/revoke-others").then(r => r.data),
};

export const auditApi = {
  list: (params = {}) => adminApi.get("/admin/audit", { params }).then(r => r.data),
  csvUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${ADMIN_BASE}/admin/audit/export.csv${qs ? `?${qs}` : ""}`;
  },
};

export const dashApi = {
  growth: () => adminApi.get("/admin/dashboard/knowledge-growth").then(r => r.data),
  summary: () => adminApi.get("/admin/dashboard/summary").then(r => r.data),
  health: () => adminApi.get("/admin/dashboard/system-health").then(r => r.data),
};

export const usersApi = {
  list: () => adminApi.get("/admin/users").then(r => r.data),
  create: (p) => adminApi.post("/admin/users", p).then(r => r.data),
  update: (id, p) => adminApi.patch(`/admin/users/${id}`, p).then(r => r.data),
  delete: (id) => adminApi.delete(`/admin/users/${id}`).then(r => r.data),
  roles: () => adminApi.get("/admin/users/roles").then(r => r.data),
};

export const flagsApi = {
  list: () => adminApi.get("/admin/feature-flags").then(r => r.data),
  update: (key, value) => adminApi.patch(`/admin/feature-flags/${key}`, { value }).then(r => r.data),
};

export function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
